import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { FaSort, FaSortUp, FaSortDown, FaChevronDown } from "react-icons/fa";

export default function DynamicTable({
  columns,
  data,
  actions,
  theadClass = "",
  tbodyClass = "",
  initialPageSize = 15,
  pageSizeOptions = [15, 20, 30, 50],
  showPagination = true,
  showRowsPerPage = false,
  showPageInfo = true,
  rightToolbar,
  rightToolbarClassName = "sm:ml-auto",
  onRowClick,
  emptyMessage = "No data available.",
  searchable = true,
  onSearch = () => {},
  searchPlaceholder = "Search...",
  showTabs = false,
  tabs = [],
  activeTab = "",
  onTabChange = () => {},
  mergeConfig = null, // New prop for row spanning
  rowStyle = null,
  tableClassName = "",
}) {
  const [sortConfig, setSortConfig] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(initialPageSize);
  const [expandedState, setExpandedState] = useState({});

  const getRowRenderKey = (row, index) => {
    if (row?.id !== undefined && row?.id !== null) {
      return `${row.id}-${index}`;
    }
    if (row?._id !== undefined && row?._id !== null) {
      return `${row._id}-${index}`;
    }
    return `row-${index}`;
  };

  // Use tab data if tabs are enabled, otherwise use regular data
  const currentTabData =
    showTabs && activeTab
      ? tabs.find((tab) => tab.key === activeTab)?.data || []
      : data;

  const filteredData = React.useMemo(() => {
    if (!searchTerm) return currentTabData;

    const lowerSearchTerm = searchTerm.toLowerCase();

    return currentTabData.filter((row) => {
      const rowString = JSON.stringify(row).toLowerCase();
      return rowString.includes(lowerSearchTerm);
    });
  }, [currentTabData, searchTerm]);

  const sortedData = React.useMemo(() => {
    if (!sortConfig) return filteredData;
    const { key, direction, sortFunction, sortValue } = sortConfig;

    return [...filteredData].sort((a, b) => {
      if (sortFunction && typeof sortFunction === "function") {
        const result = sortFunction(a, b);
        return direction === "asc" ? result : -result;
      }

      let aVal = typeof sortValue === "function" ? sortValue(a) : a[key];
      let bVal = typeof sortValue === "function" ? sortValue(b) : b[key];

      if (typeof aVal === "string" && typeof bVal === "string") {
        return direction === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      if (aVal < bVal) return direction === "asc" ? -1 : 1;
      if (aVal > bVal) return direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [filteredData, sortConfig]);

  const totalPages = Math.max(1, Math.ceil(sortedData.length / rowsPerPage));
  const paginatedData = sortedData.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage,
  );

  const handleSort = (col) => {
    if (!col.sortable) return;
    let direction = "asc";
    if (
      sortConfig &&
      sortConfig.key === col.key &&
      sortConfig.direction === "asc"
    ) {
      direction = "desc";
    }
    setSortConfig({
      key: col.key,
      direction,
      sortFunction: col.sortFunction,
      sortValue: col.sortValue,
    });
    setCurrentPage(1);
  };

  const renderSortIcon = (col) => {
    if (!col.sortable) return null;
    if (sortConfig && sortConfig.key === col.key) {
      return sortConfig.direction === "asc" ? (
        <FaSortUp className="ml-1 text-gray-400" />
      ) : (
        <FaSortDown className="ml-1 text-gray-400" />
      );
    }
    return <FaSort className="ml-1 text-gray-400" />;
  };

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handleTabChange = (tabKey) => {
    onTabChange(tabKey);
    setCurrentPage(1);
    setSearchTerm("");
    setSortConfig(null);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    onSearch(term);
    setCurrentPage(1);
  };

  const highlightText = (text) => {
    if (!searchTerm || text === null || text === undefined) {
      return text;
    }

    const searchRegex = new RegExp(`(${searchTerm})`, "gi");

    return String(text)
      .split(searchRegex)
      .map((part, index) => {
        const isMatch = part.toLowerCase() === searchTerm.toLowerCase();

        return isMatch ? (
          <mark key={index} className="bg-yellow-200 text-black px-0.5 rounded">
            {part}
          </mark>
        ) : (
          part
        );
      });
  };

  // Toggle expansion for a specific column in a row
  const toggleExpand = (rowKey, colKey) => {
    setExpandedState((prev) => {
      const rowExpanded = prev[rowKey] || {};
      const isOpen = rowExpanded[colKey];
      
      return {
        ...prev,
        [rowKey]: isOpen ? {} : { [colKey]: true },
      };
    });
  };

  const isExpanded = (rowKey, colKey) => !!expandedState[rowKey]?.[colKey];

  // Prepare rowspan data if mergeConfig is provided
  const getRowspanData = () => {
    if (!mergeConfig) return null;

    const { groupByKey, mergeColumns } = mergeConfig;
    const rowspanMap = new Map();

    // Group rows by the groupByKey
    const groupedRows = new Map();
    paginatedData.forEach((row, idx) => {
      const groupKey = row[groupByKey];
      if (!groupedRows.has(groupKey)) {
        groupedRows.set(groupKey, []);
      }
      groupedRows.get(groupKey).push({ row, originalIndex: idx });
    });

    // Calculate rowspan for each group
    for (const [groupKey, groupItems] of groupedRows.entries()) {
      const rowspan = groupItems.length;
      for (const [idx, item] of groupItems.entries()) {
        const rowKey = getRowRenderKey(item.row, item.originalIndex);
        rowspanMap.set(`${rowKey}-${groupByKey}`, rowspan);

        // Mark which columns should be shown (only first row shows)
        for (const mergeCol of mergeColumns) {
          rowspanMap.set(`${rowKey}-${mergeCol}`, idx === 0 ? rowspan : 0);
        }
      }
    }

    return rowspanMap;
  };

  const rowspanMap = getRowspanData();
  const totalCols = columns.length + (actions && actions.length > 0 ? 1 : 0);

  return (
    <div className="overflow-x-auto">
      {/* Search and Toolbar Row */}
      <div
        className="flex flex-wrap items-center justify-start mb-4 w-full gap-4 px-4 pt-4"
        onClick={(e) => e.stopPropagation()}
      >
        {searchable && (
          <div className="w-full sm:w-[40%] md:w-[20%]">
            <input
              type="text"
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              className="px-4 py-2 border rounded w-full"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}

        {rightToolbar && (
          <div className={`flex-shrink-0 ${rightToolbarClassName}`}>{rightToolbar}</div>
        )}
      </div>

      {/* Tabs Section */}
      {showTabs && tabs.length > 0 && (
        <div className="flex items-center gap-2 mb-4 border-b">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => handleTabChange(tab.key)}
              className={`px-4 py-2 rounded-t-lg border-b-2 transition-all duration-200 ${
                activeTab === tab.key
                  ? "border-blue-600 bg-white text-blue-600 font-semibold"
                  : "border-transparent bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}

      {/* Table */}
      <table
        className={`min-w-full divide-y divide-gray-200 text-sm ${tableClassName}`}
      >
        <thead className={`${theadClass === "" ? "bg-gray-50" : theadClass}`}>
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                className={`${
                  col.thClass
                    ? ` ${col.thClass}`
                    : "px-4 py-3 text-left uppercase"
                }${col.sortable ? " cursor-pointer" : ""}`}
                onClick={() => handleSort(col)}
              >
                <div className="flex items-center">
                  <span>{col.header}</span>
                  {renderSortIcon(col)}
                </div>
              </th>
            ))}
            {actions && actions.length > 0 && (
              <th className="px-4 py-3 text-left uppercase">Action</th>
            )}
          </tr>
        </thead>
        <tbody
          className={
            tbodyClass ? tbodyClass : `bg-white divide-y divide-gray-200`
          }
        >
          {paginatedData.length === 0 && (
            <tr>
              <td
                colSpan={totalCols}
                className="text-center py-4 text-gray-500"
              >
                {emptyMessage}
              </td>
            </tr>
          )}

          {paginatedData.length > 0 &&
            paginatedData.map((row, index) => {
              const rowKey = getRowRenderKey(
                row,
                (currentPage - 1) * rowsPerPage + index,
              );

              const expandedColumns = columns.filter(
                (col) => col.expandable && isExpanded(rowKey, col.key),
              );

              return (
                <React.Fragment key={rowKey}>
                  <tr
                    style={typeof rowStyle === "function" ? rowStyle(row) : {}}
                    className={
                      (typeof onRowClick === "function"
                        ? "cursor-pointer "
                        : "") + "transition-colors"
                    }
                    onClick={
                      typeof onRowClick === "function"
                        ? (e) => {
                            if (
                              e.target.closest("td .group") ||
                              e.target.closest("button") ||
                              e.target.closest("svg") ||
                              e.target.closest("path") ||
                              e.target.closest("td[data-expandable='true']")
                            ) {
                              return;
                            }
                            onRowClick(row, e);
                          }
                        : undefined
                    }
                  >
                    {columns.map((col) => {
                      // Check if this column should be merged
                      let rowspan = null;
                      if (
                        mergeConfig &&
                        mergeConfig.mergeColumns.includes(col.key)
                      ) {
                        rowspan = rowspanMap?.get(`${rowKey}-${col.key}`);
                        // If rowspan is 0, don't render this cell
                        if (rowspan === 0) return null;
                      }

                      return (
                        <td
                          key={col.key}
                          data-expandable={col.expandable ? "true" : undefined}
                          rowSpan={rowspan || undefined}
                          className={`px-4 py-2 border-b ${
                            col.tdClass ? col.tdClass : "whitespace-nowrap"
                          } ${
                            col.onCellClick || col.expandable
                              ? "cursor-pointer"
                              : ""
                          }`}
                          onClick={(e) => {
                            if (col.expandable) {
                              e.stopPropagation();
                              toggleExpand(rowKey, col.key);
                            }

                            if (col.onCellClick) {
                              col.onCellClick(row);
                            }
                          }}
                        >
                          <div className="flex items-center">
                            <div className="flex-1">
                              {col.renderCell
                                ? col.renderCell(row, searchTerm, highlightText)
                                : highlightText(row[col.key])}
                            </div>

                            {col.expandable && (
                              <FaChevronDown
                                className={`ml-2 text-gray-400 transition-transform ${
                                  isExpanded(rowKey, col.key)
                                    ? "rotate-180"
                                    : ""
                                }`}
                              />
                            )}
                          </div>
                        </td>
                      );
                    })}

                    {actions && actions.length > 0 && (
                      <td className="px-4 py-2 border-b whitespace-nowrap">
                        <div className="flex items-center justify-start gap-2 relative">
                          {actions
                            .filter((act) =>
                              typeof act.shouldShow === "function"
                                ? act.shouldShow(row)
                                : true,
                            )
                            .map((act, i) => (
                              <div key={i} className="group relative">
                                <div
                                  className={`p-2 rounded-md border bg-white transition-all duration-200 shadow-sm hover:shadow-md cursor-pointer ${
                                    act.colorClasses ||
                                    "border-gray-200 hover:border-gray-400 hover:bg-gray-50"
                                  }`}
                                  onClick={() => act.onClick(row)}
                                >
                                  {typeof act.icon === "function"
                                    ? act.icon(row)
                                    : act.icon}
                                </div>
                                <span className="absolute z-10 -top-10 left-1/2 -translate-x-1/2 whitespace-nowrap bg-gray-50 text-gray-800 text-xs font-medium px-3 py-1.5 rounded-md border border-gray-200 shadow-md opacity-0 group-hover:opacity-100 transition-all duration-200 pointer-events-none">
                                  {typeof act.label === "function"
                                    ? act.label(row)
                                    : act.label}
                                  <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2.5 h-2.5 rotate-45 bg-gray-50 border-l border-t border-gray-200" />
                                </span>
                              </div>
                            ))}
                        </div>
                      </td>
                    )}
                  </tr>

                  {expandedColumns.length > 0 && (
                    <tr className="expanded-row">
                      <td
                        colSpan={totalCols}
                        className="pb-2 px-2 border-b bg-gray-50"
                      >
                        <div className="space-y-3">
                          {expandedColumns.map((col) => (
                            <div key={col.key}>
                              {col.renderExpandedContent
                                ? col.renderExpandedContent(
                                    row,
                                    searchTerm,
                                    highlightText,
                                  )
                                : null}
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
        </tbody>
      </table>

      {showPagination && (
        <div
          className={`mt-4 flex flex-col sm:flex-row ${
            showRowsPerPage ? `justify-between` : `justify-end`
          } items-center gap-3`}
        >
          {showRowsPerPage && (
            <div className="flex items-center space-x-2">
              <label className="text-sm">Rows per page:</label>
              <select
                value={rowsPerPage}
                onChange={handleRowsPerPageChange}
                className="px-2 py-1 border rounded"
              >
                {pageSizeOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>
          )}
          {filteredData.length > rowsPerPage && (
            <div className="flex flex-wrap items-center space-x-1">
              {showPageInfo && (
                <span className="text-sm">
                  Page {currentPage} of {totalPages}
                </span>
              )}
              <button
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                disabled={currentPage === 1}
                className="px-2 py-1 border rounded disabled:opacity-50"
              >
                Previous
              </button>

              {/* Numbered Pagination */}
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(
                  (page) =>
                    page === 1 ||
                    page === totalPages ||
                    Math.abs(page - currentPage) <= 2,
                )
                .reduce((acc, page, idx, arr) => {
                  if (idx > 0 && page - arr[idx - 1] > 1) acc.push("ellipsis");
                  acc.push(page);
                  return acc;
                }, [])
                .map((item, idx) =>
                  item === "ellipsis" ? (
                    <span
                      key={`ellipsis-${idx}`}
                      className="px-2 py-1 text-gray-500"
                    >
                      ...
                    </span>
                  ) : (
                    <button
                      key={item}
                      onClick={() => setCurrentPage(item)}
                      className={`px-2 py-1 border rounded ${
                        currentPage === item ? "bg-blue-500 text-white" : ""
                      }`}
                    >
                      {item}
                    </button>
                  ),
                )}

              <button
                onClick={() =>
                  setCurrentPage((p) => Math.min(p + 1, totalPages))
                }
                disabled={currentPage === totalPages}
                className="px-2 py-1 border rounded disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

DynamicTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      header: PropTypes.string.isRequired,
      sortable: PropTypes.bool,
      renderCell: PropTypes.func,
      onCellClick: PropTypes.func,
      expandable: PropTypes.bool,
      renderExpandedContent: PropTypes.func,
      thClass: PropTypes.string,
      tdClass: PropTypes.string,
    }),
  ).isRequired,
  data: PropTypes.array.isRequired,
  theadClass: PropTypes.string,
  tbodyClass: PropTypes.string,
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      icon: PropTypes.node,
      onClick: PropTypes.func.isRequired,
      colorClasses: PropTypes.string,
      shouldShow: PropTypes.func,
    }),
  ),
  initialPageSize: PropTypes.number,
  pageSizeOptions: PropTypes.arrayOf(PropTypes.number),
  showPagination: PropTypes.bool,
  showRowsPerPage: PropTypes.bool,
  showPageInfo: PropTypes.bool,
  rightToolbar: PropTypes.node,
  onRowClick: PropTypes.func,
  emptyMessage: PropTypes.string,
  searchable: PropTypes.bool,
  onSearch: PropTypes.func,
  rowStyle: PropTypes.func,
  searchPlaceholder: PropTypes.string,
  showTabs: PropTypes.bool,
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      data: PropTypes.array.isRequired,
    }),
  ),
  activeTab: PropTypes.string,
  onTabChange: PropTypes.func,
  mergeConfig: PropTypes.shape({
    groupByKey: PropTypes.string.isRequired,
    mergeColumns: PropTypes.arrayOf(PropTypes.string).isRequired,
  }),
};
